import * as React from "react";
import { Input } from "@/shared/components/ui/input";
import { cn } from "@/shared/utils/cn";

export interface PhoneNumberInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type" | "onChange"
> {
  value?: string;
  onChange?: (value: string) => void;
}

/**
 * Formats a phone number string to (xxx)xxx-xxxx format
 */
export const formatPhoneNumber = (value: string): string => {
  // Remove all non-numeric characters
  const numbers = value.replace(/\D/g, "");

  // Limit to 10 digits
  const limited = numbers.slice(0, 10);

  // Format based on length
  if (limited.length === 0) return "";
  if (limited.length <= 3) return `(${limited}`;
  if (limited.length <= 6) return `(${limited.slice(0, 3)})${limited.slice(3)}`;
  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
};

/**
 * Strips formatting from phone number to get raw digits
 */
export const unformatPhoneNumber = (formatted: string): string => {
  return formatted.replace(/\D/g, "");
};

const PhoneNumberInput = React.forwardRef<HTMLInputElement, PhoneNumberInputProps>(
  ({ className, value = "", onChange, ...props }, ref) => {
    const [displayValue, setDisplayValue] = React.useState(() => formatPhoneNumber(value));

    // Update display value when value prop changes
    React.useEffect(() => {
      setDisplayValue(formatPhoneNumber(value));
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const formatted = formatPhoneNumber(inputValue);

      setDisplayValue(formatted);

      // Return unformatted value (just digits) to parent
      if (onChange) {
        const unformatted = unformatPhoneNumber(formatted);
        onChange(unformatted);
      }
    };

    return (
      <Input
        ref={ref}
        type="tel"
        value={displayValue}
        onChange={handleChange}
        placeholder="(123) 456-7890"
        className={cn(className)}
        {...props}
      />
    );
  },
);

PhoneNumberInput.displayName = "PhoneNumberInput";

export { PhoneNumberInput };
